import type { Session } from "@supabase/supabase-js";
import { appConfig } from "./config";

export interface LessonInvitationLesson {
  start: string;
  end: string;
}

export interface LessonInvitationInput {
  recipientEmail: string;
  studentName: string;
  lessons: LessonInvitationLesson[];
  teamsUrl?: string;
  whiteboardUrl?: string;
  lessonNote?: string;
}

export interface LessonInvitationPreview {
  subject: string;
  text: string;
  html: string;
}

export interface LessonInvitationResult {
  sent: boolean;
  subject?: string;
  lessonCount?: number;
  staging?: boolean;
  preview?: LessonInvitationPreview;
  calendarEventsCreated?: number;
  calendarEventsAlreadyExisting?: number;
}

function escapeHtml(value: string) {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\"/g, "&quot;").replace(/'/g, "&#039;");
}

function formatLesson(lesson: LessonInvitationLesson) {
  const start = new Date(lesson.start);
  const end = new Date(lesson.end);
  const weekday = new Intl.DateTimeFormat("de-DE", { timeZone: "Europe/Berlin", weekday: "long" }).format(start).toLocaleUpperCase("de-DE");
  const date = new Intl.DateTimeFormat("de-DE", { timeZone: "Europe/Berlin", day: "2-digit", month: "2-digit", year: "numeric" }).format(start);
  const time = new Intl.DateTimeFormat("de-DE", { timeZone: "Europe/Berlin", hour: "2-digit", minute: "2-digit", hour12: false });
  return { weekday, date, startTime: time.format(start), endTime: time.format(end) };
}

export function renderLessonInvitationPreview(input: LessonInvitationInput): LessonInvitationPreview {
  const count = input.lessons.length;
  const subject = count === 1 ? "Deine nächste Mathestunde bei Mathe mit Joe" : `Deine nächsten ${count} Mathestunden bei Mathe mit Joe`;
  const lessonRows = input.lessons.map((lesson) => {
    const item = formatLesson(lesson);
    return `<table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="margin-bottom:10px;background:#f8fbff;border:1px solid #dbe6fb;border-radius:14px;"><tr><td style="padding:18px;"><div style="font-size:11px;font-weight:800;letter-spacing:.08em;color:#2f6fed;">${escapeHtml(item.weekday)}</div><div style="padding-top:5px;font-size:22px;font-weight:800;color:#183153;">${escapeHtml(item.date)}</div><div style="padding-top:5px;font-size:13px;color:#64748b;">${escapeHtml(item.startTime)}–${escapeHtml(item.endTime)} Uhr</div></td></tr></table>`;
  }).join("");

  const teams = input.teamsUrl ? `<td width="50%"><a href="${escapeHtml(input.teamsUrl)}" style="display:block;text-align:center;text-decoration:none;background:#2f6fed;color:#fff;border-radius:12px;padding:15px 16px;font-size:14px;font-weight:800;">💬 Teams beitreten</a></td>` : "";
  const whiteboard = input.whiteboardUrl ? `<td width="50%"><a href="${escapeHtml(input.whiteboardUrl)}" style="display:block;text-align:center;text-decoration:none;background:#fff;color:#183153;border:1px solid #dfe5ef;border-radius:12px;padding:14px 16px;font-size:14px;font-weight:800;">✏️ Whiteboard öffnen</a></td>` : "";
  const spacer = input.teamsUrl && input.whiteboardUrl ? '<td width="12"></td>' : "";
  const actions = teams || whiteboard ? `<tr><td style="padding:6px 32px 22px 32px;"><table role="presentation" width="100%"><tr>${teams}${spacer}${whiteboard}</tr></table></td></tr>` : "";
  const noteBlock = input.lessonNote ? `<tr><td style="padding:0 32px 28px 32px;"><table role="presentation" width="100%" style="background:#e9faf4;border-radius:14px;"><tr><td style="padding:18px;"><div style="font-size:13px;font-weight:800;color:#087153;">✓ Für die Stunde</div><div style="padding-top:5px;font-size:13px;line-height:1.6;color:#45665d;">${escapeHtml(input.lessonNote)}</div></td></tr></table></td></tr>` : "";
  const calendarNotice = `<tr><td style="padding:4px 32px 18px 32px;"><table role="presentation" width="100%" style="background:#eef4ff;border:1px solid #cfdcff;border-radius:12px;"><tr><td style="padding:13px 16px;font-size:13px;line-height:1.55;color:#315f9e;"><strong>📅 Kalender</strong><br>Die aufgeführten Termine werden zusätzlich als Kalendereinladung verschickt. Teams und Whiteboard stehen auch in der Terminbeschreibung.</td></tr></table></td></tr>`;

  const html = `<!doctype html><html lang="de"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>${escapeHtml(subject)}</title></head><body style="margin:0;padding:0;background:#f7f8fb;font-family:'Segoe UI',Arial,sans-serif;color:#183153;"><table role="presentation" width="100%" style="background:#f7f8fb;"><tr><td align="center" style="padding:32px 12px;"><table role="presentation" width="600" style="width:600px;max-width:100%;background:#fff;border:1px solid #dfe5ef;border-radius:20px;overflow:hidden;"><tr><td style="padding:24px 32px;background:#183153;color:#fff;"><strong>Mathe mit Joe</strong><div style="font-size:11px;color:#a9c4ff;margin-top:3px;">Persönliche 1:1 Online-Nachhilfe</div></td></tr><tr><td style="padding:34px 32px 18px;"><h1 style="margin:0 0 10px;font-size:30px;color:#183153;">Hallo ${escapeHtml(input.studentName)} 👋</h1><p style="margin:0;color:#64748b;line-height:1.7;">hier findest du deine nächsten Mathestunden. Über die Links unten kommst du direkt zu unserer Stunde.</p></td></tr><tr><td style="padding:10px 32px 12px;">${lessonRows}</td></tr>${calendarNotice}${actions}${noteBlock}<tr><td style="padding:0 32px 32px;color:#64748b;line-height:1.6;">Bis dann! 😊<br>Joe<br><span style="font-size:12px;">Mathe mit Joe</span></td></tr></table></td></tr></table></body></html>`;

  const plainLessons = input.lessons.map((lesson) => {
    const item = formatLesson(lesson);
    return `• ${item.date}, ${item.startTime}–${item.endTime} Uhr`;
  }).join("\n");
  const text = `Hallo ${input.studentName} 👋\n\n${plainLessons}\n\nDie Termine werden zusätzlich als Kalendereinladung verschickt.\n${input.teamsUrl ? `Teams: ${input.teamsUrl}\n` : ""}${input.whiteboardUrl ? `Whiteboard: ${input.whiteboardUrl}\n` : ""}\nBis dann! 😊\nJoe\nMathe mit Joe`;
  return { subject, text, html };
}

export async function sendLessonInvitation(session: Session, input: LessonInvitationInput, options: { preview?: boolean } = {}): Promise<LessonInvitationResult> {
  if (!appConfig.isConfigured) throw new Error("Supabase ist nicht konfiguriert.");
  const response = await fetch(`${appConfig.supabaseUrl}/functions/v1/send-lesson-invitation`, {
    method: "POST",
    headers: {
      apikey: appConfig.supabaseAnonKey,
      Authorization: `Bearer ${session.access_token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ ...input, preview: options.preview === true }),
  });
  const payload = await response.json().catch(() => ({})) as LessonInvitationResult & { error?: string; code?: string };
  if (!response.ok) throw new Error(payload.error ?? "Die Einladung konnte nicht verarbeitet werden.");
  return payload;
}
